import { useMemo } from 'react';
import { useResolveTeamIdentityId } from '@/common/hooks/useResolveIdentityId';
import { useUserIntegrations } from '@/common/hooks/useUserIntegrations';
import { sanitizeFilterQueryValue } from '@/common/utils/sanitizeFilterQueryValue';
import { useCore, useIdentity } from '$api-clients/hooks';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';
import { buildAgentQuery } from './buildAgentQuery';
import { CONTACT_SUGGESTION_GROUPS } from './groups';
import {
  mapFollowUpBossContactToContactSuggestion,
  mapGoogleContactToContactSuggestion,
  mapTeamMemberToContactSuggestion,
  type RestTeamMember,
} from './mappers';
import type { ContactSuggestion, ContactSuggestionResult } from './types';

interface UseContactSuggestionsParams {
  userId: string;
  teamId?: string | null;
  searchValue?: string;
  limit?: number;
  includeGoogleContacts: boolean;
  includeFollowUpBossContacts: boolean;
  includeSideContacts: boolean;
  includeTeamContacts: boolean;
  includeReferralAgents?: boolean;
  excludeNonCompliantTeams?: boolean;
  licenseStates?: string[];
  skip?: boolean;
}

interface ContactSuggestions {
  suggestions: ContactSuggestion[];
  loading: boolean;
}

interface ContactSuggestionGroupArrays {
  teamAgents: ContactSuggestionResult[];
  sideAgents: ContactSuggestionResult[];
  googlePeople: ContactSuggestionResult[];
  followUpBossPeople: ContactSuggestionResult[];
}

/**
 * Drops external (Google/FUB) contacts whose email matches an agent's, then tags
 * each with its group label.
 */
function assembleSuggestions({
  teamAgents: rawTeamAgents,
  sideAgents: rawSideAgents,
  googlePeople: rawGooglePeople,
  followUpBossPeople: rawFollowUpBossPeople,
}: ContactSuggestionGroupArrays): ContactSuggestion[] {
  const teamAgents = rawTeamAgents.map((contact) => ({
    ...contact,
    group: CONTACT_SUGGESTION_GROUPS.teamContacts,
  }));
  const sideAgents = rawSideAgents.map((contact) => ({
    ...contact,
    group: CONTACT_SUGGESTION_GROUPS.otherContacts,
  }));

  const allAgents = [...teamAgents, ...sideAgents];

  const dedupeAgainstAgents = (
    contacts: ContactSuggestionResult[],
    group: string,
  ) =>
    contacts.reduce<ContactSuggestion[]>((acc, contact) => {
      const normalizedEmail = contact.email?.toLowerCase();
      const isDuplicateContact =
        !!normalizedEmail &&
        allAgents.some(({ email }) => email?.toLowerCase() === normalizedEmail);

      if (isDuplicateContact) {
        return acc;
      }

      acc.push({ ...contact, group });
      return acc;
    }, []);

  return [
    ...teamAgents,
    ...sideAgents,
    ...dedupeAgainstAgents(
      rawGooglePeople,
      CONTACT_SUGGESTION_GROUPS.googleContacts,
    ),
    ...dedupeAgainstAgents(
      rawFollowUpBossPeople,
      CONTACT_SUGGESTION_GROUPS.followUpBossContacts,
    ),
  ];
}

/**
 * Builds the team-agents query — agents on the current team (`team.id[eq]`).
 * `teamUuid` is required; the caller only invokes this once the current team
 * has resolved to a UUID.
 */
function buildTeamAgentsQuery(params: {
  searchValue: string;
  includeReferralAgents: boolean;
  licenseStates: string[];
  includeOnboardingAgents: boolean;
  teamUuid: string;
}): string {
  const { teamUuid, ...rest } = params;
  return buildAgentQuery({
    ...rest,
    teamScope: { teamId: teamUuid, mode: 'only' },
  });
}

/**
 * Builds the side-agents query — agents NOT on the current team. `teamId` is the
 * resolved team UUID, or null when there is no current team (in which case every
 * agent is a side agent, so the query is unscoped). Callers must not invoke this
 * while a current team is still resolving to a UUID.
 */
function buildSideAgentsQuery(params: {
  searchValue: string;
  includeReferralAgents: boolean;
  licenseStates: string[];
  includeOnboardingAgents: boolean;
  teamId: string | null;
}): string {
  const { teamId, ...rest } = params;
  if (!teamId) {
    return buildAgentQuery(rest);
  }
  return buildAgentQuery({
    ...rest,
    teamScope: { teamId, mode: 'exclude' },
  });
}

/**
 * Fetches the four suggestion groups (team agents, side agents, Google people,
 * Follow Up Boss people) via direct identity + core calls, drops external
 * contacts that duplicate an agent by email, and returns one grouped list ready
 * for the autocomplete.
 */
export function useContactSuggestions({
  userId,
  teamId,
  searchValue = '',
  limit = 10,
  includeGoogleContacts,
  includeFollowUpBossContacts,
  includeSideContacts,
  includeTeamContacts,
  includeReferralAgents = false,
  excludeNonCompliantTeams = false,
  licenseStates = [],
  skip: skipProp = false,
}: UseContactSuggestionsParams): ContactSuggestions {
  const someResultsIncluded =
    includeGoogleContacts ||
    includeFollowUpBossContacts ||
    includeSideContacts ||
    includeTeamContacts;
  const skip = skipProp || !someResultsIncluded;
  const hasUser = !skip && !!userId;
  // Sanitized here only to gate the fetch on non-empty input; `buildAgentQuery`
  // is the canonical sanitization point for the query string itself.
  const trimmedSearch = sanitizeFilterQueryValue(searchValue);
  const hasAgentSearch = hasUser && trimmedSearch.length > 0;
  const hasExternalSearch = hasUser && searchValue.trim().length > 0;
  const shouldFetchAgents = includeSideContacts || includeTeamContacts;
  // `/team-members` has no compliance filter field, so compliance is filtered
  // client-side; overfetch so that filter + slice-to-limit can't starve a group
  // below `limit`.
  const agentFetchLimit = limit * 2;

  const { isIntegrationValid, loading: integrationsLoading } =
    useUserIntegrations(hasUser ? userId : undefined);
  const isGoogleLinked = isIntegrationValid('googlePeople');
  const isFollowUpBossLinked = isIntegrationValid('followUpBoss');

  const { hasPermission: includeOnboardingAgents } = useHasPermission({
    roles: ['ADMIN'],
    scopes: [Scopes.INTERACTIONS_WRITE],
  });

  // The `/team-members` filter accepts `team.id` by UUID only, so resolve a
  // possibly-legacy current team id first.
  const shouldResolveTeam = !skip && shouldFetchAgents && !!teamId;
  const { resolvedId: teamUuid, isLoading: teamResolving } =
    useResolveTeamIdentityId(shouldResolveTeam ? teamId : undefined);

  const teamAgentsQuery =
    hasAgentSearch && includeTeamContacts && teamUuid
      ? buildTeamAgentsQuery({
          searchValue,
          includeReferralAgents,
          licenseStates,
          includeOnboardingAgents,
          teamUuid,
        })
      : null;
  const { data: teamAgentsData, isLoading: teamAgentsLoading } = useIdentity(
    '/team-members',
    teamAgentsQuery
      ? { params: { query: { q: teamAgentsQuery, limit: agentFetchLimit } } }
      : null,
    { keepPreviousData: true },
  );

  // Only query side agents once a current team (if any) has resolved to a UUID,
  // so `teamUuid` is unambiguous: null then means "no current team" (fetch
  // unscoped), non-null means "exclude this team". While a provided team is
  // unresolved (still loading or unresolvable), skip rather than fetch the wrong
  // set.
  const teamResolved = !teamId || !!teamUuid;
  const sideAgentsQuery =
    hasAgentSearch && includeSideContacts && teamResolved
      ? buildSideAgentsQuery({
          searchValue,
          includeReferralAgents,
          licenseStates,
          includeOnboardingAgents,
          teamId: teamUuid,
        })
      : null;
  const { data: sideAgentsData, isLoading: sideAgentsLoading } = useIdentity(
    '/team-members',
    sideAgentsQuery
      ? { params: { query: { q: sideAgentsQuery, limit: agentFetchLimit } } }
      : null,
    { keepPreviousData: true },
  );

  const canSearchGoogle =
    hasExternalSearch && includeGoogleContacts && isGoogleLinked;
  const { data: googleData, isLoading: googleLoading } = useCore(
    '/user-settings/{id}/google-contact/search',
    canSearchGoogle
      ? {
          params: {
            path: { id: userId },
            query: { searchValue, pageSize: limit },
          },
        }
      : null,
    { keepPreviousData: true },
  );

  const canSearchFollowUpBoss =
    hasExternalSearch && includeFollowUpBossContacts && isFollowUpBossLinked;
  const { data: followUpBossData, isLoading: followUpBossLoading } = useCore(
    '/users/{id}/integrations/follow-up-boss/contacts',
    canSearchFollowUpBoss
      ? { params: { path: { id: userId }, query: { searchValue, limit } } }
      : null,
    { keepPreviousData: true },
  );

  const agents = useMemo(() => {
    const isCompliant = (member: RestTeamMember) =>
      excludeNonCompliantTeams ? member.team.isCompliant : true;

    const toSuggestions = (rows: RestTeamMember[]) =>
      rows
        .filter(isCompliant)
        .map(mapTeamMemberToContactSuggestion)
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .slice(0, limit);

    return {
      teamAgents: toSuggestions(teamAgentsData?.data ?? []),
      sideAgents: toSuggestions(sideAgentsData?.data ?? []),
    };
  }, [teamAgentsData, sideAgentsData, limit, excludeNonCompliantTeams]);

  const googlePeople = useMemo(
    () => (googleData ?? []).map(mapGoogleContactToContactSuggestion),
    [googleData],
  );

  const followUpBossPeople = useMemo(
    () =>
      (followUpBossData?.results ?? []).map(
        mapFollowUpBossContactToContactSuggestion,
      ),
    [followUpBossData],
  );

  const loading =
    integrationsLoading ||
    teamResolving ||
    teamAgentsLoading ||
    sideAgentsLoading ||
    googleLoading ||
    followUpBossLoading;

  const suggestions = useMemo<ContactSuggestion[]>(() => {
    if (!searchValue.trim()) return [];

    return assembleSuggestions({
      teamAgents: agents.teamAgents,
      sideAgents: agents.sideAgents,
      googlePeople,
      followUpBossPeople,
    });
  }, [searchValue, agents, googlePeople, followUpBossPeople]);

  return {
    suggestions,
    loading,
  };
}
